import { IconNames } from '@blueprintjs/icons';
import { BenefitTypes } from '@strata/constants';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { InlineMessage } from '@strata/react-inline-message';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { ContractTypeOptions } from 'services/constants';
import { nullOnEmpty } from 'services/Helpers';

dayjs.extend(minMax);
dayjs.extend(utc);

const NO_MARKET = 'NO_MARKET';

const LineOfBusinessOptions = ['Commercial', 'Medicare', 'Medicaid', 'Exchange'].map((name) => ({
    name,
    value: name.toUpperCase(),
}));

/** Only show input labels on first row */
const shouldShowLabel = (label, index) => (index === 0 ? label : undefined);

const UNIQUE = 'Unique';

/** Check for capabilities with same attributes */
const dupeCapabilityCheck = (cap, capsForBenefit) =>
    capsForBenefit?.findIndex(
        (c) =>
            c.history_id !== cap.history_id &&
            c.segment_id === cap.segment_id &&
            nullOnEmpty(c.line_of_business) === nullOnEmpty(cap.line_of_business) &&
            nullOnEmpty(c.lob_segment) === nullOnEmpty(cap.lob_segment) &&
            nullOnEmpty(c.contract_type) === nullOnEmpty(cap.contract_type),
    ) === -1;

export const PayerCapabilityRow = ({
    segmentsForPayer = undefined,
    marketOptions = undefined,
    benefit,
    index,
    cap,
    arrayHelpers,
}) => {
    const formik = useFormikContext();
    const rowId = `${arrayHelpers.name}.${index}`;
    return (
        <>
            <div className="sds_payer-capabilities__form-row">
                <SuggestSingleSelectFormik
                    id={`${rowId}.segment_id`}
                    label={shouldShowLabel('Payer Segment', index)}
                    required
                    disabled={!cap.is_new}
                    values={
                        // Filter out dupe segments for pharmacy benefit
                        benefit === BenefitTypes.PHARMACY
                            ? segmentsForPayer?.filter(
                                  (segment) =>
                                      !getIn(formik.values, arrayHelpers.name).find(
                                          (c) => c.segment_id === segment.segment_id,
                                      ),
                              )
                            : segmentsForPayer
                    }
                    selectedItem={segmentsForPayer?.find(
                        (segment) => segment.segment_id === cap.segment_id,
                    )}
                    onChange={(newVal) =>
                        arrayHelpers.replace(index, {
                            ...cap,
                            segment_id: newVal?.segment_id,
                        })
                    }
                />
                {benefit === BenefitTypes.MEDICAL && (
                    <>
                        <SuggestSingleSelectFormik
                            id={`${rowId}.line_of_business`}
                            label={shouldShowLabel('Line Of Business', index)}
                            disabled={!cap.is_new}
                            values={LineOfBusinessOptions}
                            selectedItem={LineOfBusinessOptions?.find(
                                ({ value }) => value === cap.line_of_business,
                            )}
                            onChange={(newVal) =>
                                arrayHelpers.replace(index, {
                                    ...cap,
                                    line_of_business: newVal?.value,
                                })
                            }
                        />
                        <SuggestSingleSelectFormik
                            id={`${rowId}.lob_segment`}
                            label={shouldShowLabel('Market', index)}
                            disabled={!cap.is_new}
                            values={marketOptions}
                        />
                        <SuggestSingleSelectFormik
                            id={`${rowId}.contract_type`}
                            label={shouldShowLabel('Contract Type', index)}
                            disabled={!cap.is_new}
                            values={ContractTypeOptions}
                            selectedItem={ContractTypeOptions?.find(
                                ({ value }) => value === cap.contract_type,
                            )}
                            onChange={(newVal) =>
                                arrayHelpers.replace(index, {
                                    ...cap,
                                    contract_type: newVal?.value,
                                })
                            }
                        />
                    </>
                )}
                <DateInputFormik
                    id={`${rowId}.start_date`}
                    label={shouldShowLabel('Start Date', index)}
                    required
                    disabled={!cap.is_new}
                    minDate={cap.is_new ? dayjs.utc().startOf('day').toDate() : undefined}
                />
                <DateInputFormik
                    id={`${rowId}.end_date`}
                    label={shouldShowLabel('End Date', index)}
                    minDate={dayjs.max(dayjs(cap.start_date), dayjs()).startOf('day').toDate()}
                />
                <div>
                    <Icon
                        icon={IconNames.TRASH}
                        intent={Icon.Intent.SECONDARY}
                        title="Delete"
                        className={index === 0 ? 'margin__top--large' : 'margin__top--small'}
                        onClick={async () => {
                            arrayHelpers.remove(index);
                            if (!cap.is_new) {
                                await formik.setFieldValue('capabilitiesToRemove', [
                                    ...formik.values.capabilitiesToRemove,
                                    cap,
                                ]);
                            }
                        }}
                    />
                </div>
            </div>
            {getIn(formik.errors, rowId) === UNIQUE && (
                <div className="margin__bottom--small">
                    <InlineMessage
                        intent={InlineMessage.Intent.ERROR}
                        label="A capability with these attributes already exists."
                    />
                </div>
            )}
            <CheckboxListFormik
                name={`${rowId}.no_market`}
                label=""
                required
                inline
                options={[
                    {
                        value: NO_MARKET,
                        text: 'No Live Referral events and inactive Follow On route',
                    },
                ]}
                values={cap.no_market ? [NO_MARKET] : []}
                onChange={(newValue) =>
                    arrayHelpers.replace(index, {
                        ...cap,
                        no_market: newValue?.includes(NO_MARKET),
                    })
                }
            />
        </>
    );
};

PayerCapabilityRow.validationSchema = Yup.object({
    segment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    line_of_business: Yup.string().nullable(),
    lob_segment: Yup.string().nullable(),
    contract_type: Yup.string().nullable(),
    no_market: Yup.bool().required(validationStrings.requiredField.errorMessage),
    start_date: DateInputFormik.validator()
        .required(validationStrings.requiredField.errorMessage)
        .when('is_new', {
            is: true,
            then: (schema) =>
                schema.min(dayjs.utc().startOf('day').toISOString(), 'Past date not allowed.'),
        }),
    end_date: DateInputFormik.validator()
        .nullable()
        .test(
            'end_date',
            'Must be after start date.',
            (endDate, context) =>
                !endDate ||
                !dayjs(endDate).isBefore(
                    dayjs.max(dayjs(context.parent.start_date), dayjs()).startOf('day'),
                ),
        ),
}).test(UNIQUE, UNIQUE, (cap, context) => dupeCapabilityCheck(cap, context.parent));

PayerCapabilityRow.propTypes = {
    segmentsForPayer: PropTypes.arrayOf(PropTypes.shape({})),
    marketOptions: PropTypes.arrayOf(PropTypes.string),
    benefit: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    cap: PropTypes.shape({
        segment_id: PropTypes.string,
        line_of_business: PropTypes.string,
        lob_segment: PropTypes.string,
        contract_type: PropTypes.string,
        no_market: PropTypes.bool.isRequired,
        is_new: PropTypes.bool,
        start_date: PropTypes.string,
    }).isRequired,
    arrayHelpers: PropTypes.shape({
        name: PropTypes.string.isRequired,
        remove: PropTypes.func.isRequired,
        replace: PropTypes.string.isRequired,
    }).isRequired,
};
